import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCoop } from "../contexts/CoopContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { useSocial } from "../contexts/SocialContext";
import { isClassUnlocked } from "../data/profile";
import { CLASSES } from "../data/classes";
import { ELEMENTS } from "../utils/gameLogic";
import { SFX } from "../utils/audio";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Crown, LogOut, Play, Plus, UserPlus, Users, Wifi, WifiOff,
  Check, X, Send, MessageCircle, Swords, Circle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  connected: { text: "Connecté", color: "text-emerald-400", Icon: Wifi },
  connecting: { text: "Connexion…", color: "text-yellow-400", Icon: Wifi },
  disconnected: { text: "Hors-ligne", color: "text-rose-400", Icon: WifiOff },
};

const Lobby = () => {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { profile } = useProfile();
  const {
    lobby, isHost, wsStatus, chatMessages, incomingInvites,
    createLobby, joinLobby, leaveLobby, selectClass, startGame, sendChat,
    acceptInvite, declineInvite, refreshMyLobby, inviteFriend,
  } = useCoop();
  const { isOnline, openProfile } = useSocial();

  const [lobbyName, setLobbyName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [error, setError] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [friendsList, setFriendsList] = useState([]);

  useEffect(() => { refreshMyLobby(); }, [refreshMyLobby]);

  const fetchFriendsList = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/friends`, { withCredentials: true });
      setFriendsList(res.data.friends || []);
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchFriendsList(); }, [fetchFriendsList]);

  const handleInviteFriend = async (f) => {
    SFX.click();
    try {
      await inviteFriend(f.user_id);
      toast.success(`Invitation envoyée à ${f.name} !`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'invitation");
    }
  };

  // Auto-navigate to /coop-map once the lobby is active (coop game started)
  useEffect(() => {
    if (lobby?.status === "active") {
      navigate("/coop-map");
    }
  }, [lobby?.status, navigate]);

  const me = useMemo(
    () => lobby?.members?.find((m) => m.user_id === user?.user_id) || null,
    [lobby, user]
  );

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 bg-dots flex items-center justify-center">
        <div className="text-center max-w-md">
          <Users className="w-16 h-16 mx-auto text-sky-400 mb-4" />
          <h1 className="font-display text-3xl font-bold mb-3 text-white">Mode Co-op</h1>
          <p className="text-slate-300 mb-5">Connecte-toi pour jouer en co-op jusqu'à 4 joueurs et inviter tes amis.</p>
          <button
            data-testid="lobby-login-btn"
            onClick={loginWithGoogle}
            className="bg-white text-slate-950 border-4 border-slate-950 rounded-2xl px-5 py-3 font-display font-bold cartoon-shadow"
          >
            Se connecter
          </button>
          <div className="mt-4">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white text-sm">← Retour au menu</button>
          </div>
        </div>
      </div>
    );
  }

  const handleCreate = async () => {
    SFX.click();
    setError(null);
    try { await createLobby(lobbyName.trim()); } catch (e) { setError(e?.response?.data?.detail || "Erreur"); }
  };

  const handleJoin = async () => {
    SFX.click();
    setError(null);
    const code = joinCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(code)) { setError("Code invalide (6 caractères A-Z/0-9)"); return; }
    try { await joinLobby(code); } catch (e) { setError(e?.response?.data?.detail || "Erreur"); }
  };

  const handleAccept = async (inv) => {
    SFX.click();
    try { await acceptInvite(inv); } catch (e) { setError(e?.response?.data?.detail || "Erreur"); }
  };

  const handleCopyCode = () => {
    if (!lobby?.code) return;
    try {
      navigator.clipboard.writeText(lobby.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {}
  };

  // ===== Not in a lobby: show create/join =====
  if (!lobby) {
    return (
      <div className="h-screen w-screen bg-slate-900 bg-dots overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              data-testid="back-to-menu-lobby"
              onClick={() => navigate("/")}
              className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> Menu
            </button>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Mode Co-op</h1>
            <div className="w-20" />
          </div>

          {/* Incoming invites */}
          {incomingInvites.length > 0 && (
            <div data-testid="invites-section" className="bg-amber-900/30 border-4 border-amber-500 rounded-2xl p-4 mb-6 cartoon-shadow-lg-static">
              <h2 className="font-display text-lg font-bold text-amber-300 mb-3 flex items-center gap-2">
                <UserPlus className="w-5 h-5" /> Invitations ({incomingInvites.length})
              </h2>
              <div className="space-y-2">
                {incomingInvites.map((inv) => (
                  <div key={inv.invite_id} data-testid={`invite-${inv.invite_id}`} className="flex items-center justify-between bg-slate-800 border-2 border-slate-950 rounded-xl p-3">
                    <div>
                      <p className="font-bold text-white">{inv.from_name}</p>
                      <p className="text-xs text-slate-400">Code: {inv.code} • {inv.members_count}/4 joueurs</p>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`accept-invite-${inv.invite_id}`} onClick={() => handleAccept(inv)} className="bg-emerald-500 text-white border-2 border-slate-950 rounded-lg p-2"><Check className="w-4 h-4" /></button>
                      <button data-testid={`decline-invite-${inv.invite_id}`} onClick={() => declineInvite(inv.invite_id)} className="bg-rose-500 text-white border-2 border-slate-950 rounded-lg p-2"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-5 cartoon-shadow-lg-static">
              <h2 className="font-display text-xl font-bold mb-3 text-sky-400 flex items-center gap-2">
                <Plus className="w-5 h-5" /> Créer un lobby
              </h2>
              <input
                data-testid="lobby-name-input"
                type="text"
                placeholder="Nom du lobby (optionnel)"
                maxLength={40}
                value={lobbyName}
                onChange={(e) => setLobbyName(e.target.value)}
                className="w-full bg-slate-900 text-white border-2 border-slate-950 rounded-lg px-3 py-2 mb-3"
              />
              <button
                data-testid="create-lobby-btn"
                onClick={handleCreate}
                className="w-full bg-sky-400 text-slate-950 font-display font-bold py-3 rounded-xl border-4 border-slate-950 cartoon-shadow"
              >
                Créer
              </button>
            </div>

            <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-5 cartoon-shadow-lg-static">
              <h2 className="font-display text-xl font-bold mb-3 text-emerald-400 flex items-center gap-2">
                <Users className="w-5 h-5" /> Rejoindre un lobby
              </h2>
              <input
                data-testid="join-code-input"
                type="text"
                placeholder="Code à 6 caractères"
                maxLength={6}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="w-full bg-slate-900 text-white border-2 border-slate-950 rounded-lg px-3 py-2 mb-3 font-mono tracking-widest text-center text-xl"
              />
              <button
                data-testid="join-lobby-btn"
                onClick={handleJoin}
                className="w-full bg-emerald-400 text-slate-950 font-display font-bold py-3 rounded-xl border-4 border-slate-950 cartoon-shadow"
              >
                Rejoindre
              </button>
            </div>
          </div>

          {error && <p data-testid="lobby-error" className="mt-4 text-rose-400 text-center font-bold">{error}</p>}
        </div>
      </div>
    );
  }

  // ===== In a lobby: waiting room =====
  const statusInfo = STATUS_LABEL[wsStatus] || STATUS_LABEL.disconnected;
  const StatusIcon = statusInfo.Icon;
  const allReady = lobby.members.length > 0 && lobby.members.every((m) => m.ready && m.classe_id);

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots overflow-y-auto">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <button
            data-testid="leave-lobby-btn"
            onClick={() => { SFX.click(); leaveLobby().then(() => navigate("/")); }}
            className="bg-rose-500 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Quitter
          </button>
          <div className="text-center flex-1">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white truncate">{lobby.name}</h1>
            <div className="flex items-center justify-center gap-3 mt-1">
              <button
                data-testid="lobby-code-display"
                onClick={handleCopyCode}
                className="inline-flex items-center gap-2 bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-lg px-3 py-1 font-mono font-bold tracking-widest hover:bg-yellow-300"
                title="Cliquer pour copier"
              >
                {lobby.code} <Copy className="w-3 h-3" />
              </button>
              {copied && <span className="text-emerald-400 text-xs font-bold">Copié !</span>}
              <span className={`inline-flex items-center gap-1 text-xs ${statusInfo.color}`}>
                <StatusIcon className="w-3 h-3" /> {statusInfo.text}
              </span>
            </div>
          </div>
          <div className="w-20" />
        </div>

        {/* Members grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {Array.from({ length: 4 }).map((_, i) => {
            const m = lobby.members[i];
            if (!m) {
              return (
                <div key={`empty-${i}`} className="bg-slate-800/40 border-4 border-dashed border-slate-700 rounded-2xl p-4 flex flex-col items-center justify-center text-slate-500 min-h-[180px]">
                  <UserPlus className="w-10 h-10 opacity-40 mb-1" />
                  <p className="text-xs">Place libre</p>
                </div>
              );
            }
            const cls = m.classe_id ? CLASSES[m.classe_id] : null;
            const isMe = m.user_id === user.user_id;
            return (
              <div key={m.user_id} data-testid={`member-${m.user_id}`} className={`bg-slate-800 border-4 ${m.ready ? "border-emerald-400" : "border-slate-950"} rounded-2xl p-3 cartoon-shadow-lg-static flex flex-col items-center text-center relative`}>
                {m.is_host && (
                  <div className="absolute -top-3 -right-2 bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-lg px-2 py-0.5 text-[10px] font-bold flex items-center gap-1">
                    <Crown className="w-3 h-3" /> HOST
                  </div>
                )}
                <div className="flex items-center gap-2 mb-2 w-full justify-center">
                  {m.picture && <img src={m.picture} alt={m.name} className="w-8 h-8 rounded-full border-2 border-slate-950" />}
                  <p className="font-bold text-white text-sm truncate flex-1 text-left">{m.name} {isMe && <span className="text-sky-400 text-xs">(toi)</span>}</p>
                </div>
                {cls ? (
                  <>
                    <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${cls.couleur} border-4 border-slate-950 overflow-hidden mb-1`}>
                      <img src={cls.image} alt={cls.nom} className="w-full h-full object-cover" />
                    </div>
                    <p className="font-display text-xs font-bold leading-tight">{cls.nom}</p>
                    <span className={`mt-1 inline-block ${ELEMENTS[cls.element].couleur} ${ELEMENTS[cls.element].textColor} border border-slate-950 rounded px-1.5 text-[10px] font-bold`}>
                      {ELEMENTS[cls.element].icon} {ELEMENTS[cls.element].nom}
                    </span>
                  </>
                ) : (
                  <div className="w-20 h-20 rounded-xl border-4 border-dashed border-slate-700 flex items-center justify-center text-slate-600 text-3xl">?</div>
                )}
                <div className={`mt-2 text-[10px] font-bold ${m.connected ? "text-emerald-400" : "text-slate-500"}`}>
                  {m.connected ? "● en ligne" : "○ hors-ligne"}
                </div>
                <div className={`mt-1 text-[10px] font-bold ${m.ready ? "text-emerald-400" : "text-amber-400"}`}>
                  {m.ready ? "✓ Prêt" : "En attente…"}
                </div>
              </div>
            );
          })}
        </div>

        {/* Inviter des amis directement depuis le lobby */}
        <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-4 cartoon-shadow-lg-static mb-5">
          <h2 className="font-display text-lg font-bold mb-3 text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-fuchsia-400" /> Inviter des amis
          </h2>
          {friendsList.length === 0 ? (
            <p className="text-sm text-slate-400">Tu n'as pas encore d'amis. Ajoute-en depuis la page <button onClick={() => navigate("/friends")} className="text-sky-300 underline">Amis</button> pour les inviter ici.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {friendsList.map((f) => {
                const inLobby = lobby.members.some((m) => m.user_id === f.user_id);
                return (
                  <div key={f.user_id} data-testid={`lobby-friend-${f.user_id}`} className="flex items-center gap-2 bg-slate-900 border-2 border-slate-950 rounded-xl p-2">
                    <button onClick={() => openProfile(f.user_id)} className="relative flex-shrink-0">
                      {f.picture ? (
                        <img src={f.picture} alt="" className="w-9 h-9 rounded-full border-2 border-slate-950" />
                      ) : (
                        <div className="w-9 h-9 rounded-full border-2 border-slate-950 bg-slate-700 flex items-center justify-center text-sm font-bold">{f.name?.[0] || "?"}</div>
                      )}
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-900 ${isOnline(f.user_id) ? "bg-emerald-400" : "bg-slate-600"}`} />
                    </button>
                    <p className="flex-1 min-w-0 text-sm font-bold truncate">{f.name}</p>
                    {inLobby ? (
                      <span className="text-[11px] text-emerald-400 font-bold px-2">Déjà là ✓</span>
                    ) : (
                      <button data-testid={`lobby-invite-${f.user_id}`} onClick={() => handleInviteFriend(f)}
                        className="bg-fuchsia-500 text-white border-2 border-slate-950 rounded-lg px-2.5 py-1.5 text-xs font-bold cartoon-shadow flex items-center gap-1">
                        <Send className="w-3 h-3" /> Inviter
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Class picker */}
        <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-4 cartoon-shadow-lg-static mb-5">
          <h2 className="font-display text-lg font-bold mb-3 text-white">Choisis ta classe</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {Object.values(CLASSES).map((c) => {
              const isMine = me?.classe_id === c.id;
              // En coop, plusieurs joueurs peuvent choisir la même classe (jeu visuel coopératif).
              const locked = !isClassUnlocked(c.id, profile?.level || 1, profile?.cosmetics_owned || []);
              return (
                <button
                  key={c.id}
                  data-testid={`lobby-pick-${c.id}`}
                  onClick={() => { if (locked) return; SFX.hover(); selectClass(c.id); }}
                  disabled={locked}
                  className={`relative bg-slate-900 border-4 ${isMine ? "border-yellow-400" : "border-slate-950"} rounded-xl p-2 transition-transform hover:-translate-y-0.5 ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
                  title={locked ? "Classe verrouillée — débloque-la en mode solo" : c.nom}
                >
                  <div className={`w-full aspect-square rounded-lg bg-gradient-to-br ${c.couleur} border-2 border-slate-950 overflow-hidden mb-1`}>
                    <img src={c.image} alt={c.nom} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-[10px] font-display font-bold leading-tight">{c.nom.replace("Le ", "").replace("L'", "")}</p>
                  {locked && (
                    <span className="absolute top-1 right-1 bg-slate-900/90 border-2 border-yellow-300 rounded-full px-1 text-[10px]">🔒</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Start button (host only) */}
        {isHost && (
          <button
            data-testid="start-game-btn"
            onClick={() => { SFX.click(); startGameAction(); }}
            disabled={!allReady}
            className={`w-full font-display text-xl font-bold uppercase tracking-wider py-4 rounded-2xl border-4 border-slate-950 flex items-center justify-center gap-2 ${allReady ? "bg-emerald-400 text-slate-950 cartoon-shadow" : "bg-slate-700 text-slate-500 cursor-not-allowed"}`}
          >
            <Play className="w-6 h-6" /> {allReady ? "Démarrer l'aventure" : "En attente des autres joueurs…"}
          </button>
        )}
        {!isHost && (
          <p className="text-center text-slate-400 text-sm">L'hôte démarrera la partie quand tout le monde sera prêt.</p>
        )}

        {/* Chat */}
        <div className="mt-5">
          <button
            data-testid="toggle-chat-btn"
            onClick={() => setChatOpen((v) => !v)}
            className="bg-slate-700 text-white border-2 border-slate-950 rounded-xl px-3 py-2 text-sm flex items-center gap-2"
          >
            <MessageCircle className="w-4 h-4" /> {chatOpen ? "Masquer chat" : `Chat (${chatMessages.length})`}
          </button>
          {chatOpen && (
            <div className="mt-2 bg-slate-800 border-4 border-slate-950 rounded-2xl p-3">
              <div data-testid="chat-messages" className="h-40 overflow-y-auto text-sm space-y-1 mb-2 bg-slate-900 rounded-lg p-2">
                {chatMessages.length === 0 ? (
                  <p className="text-slate-500 text-center text-xs italic">Aucun message pour l'instant…</p>
                ) : chatMessages.map((m, i) => (
                  <p key={i}><span className="text-sky-400 font-bold">{m.from_name}:</span> <span className="text-slate-200">{m.text}</span></p>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (chatInput.trim()) { sendChat(chatInput.trim()); setChatInput(""); } }} className="flex gap-2">
                <input
                  data-testid="chat-input"
                  type="text"
                  placeholder="Message…"
                  maxLength={240}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  className="flex-1 bg-slate-900 text-white border-2 border-slate-950 rounded-lg px-3 py-2 text-sm"
                />
                <button data-testid="chat-send-btn" type="submit" className="bg-sky-400 text-slate-950 border-2 border-slate-950 rounded-lg px-3 py-2"><Send className="w-4 h-4" /></button>
              </form>
            </div>
          )}
        </div>

        {error && <p data-testid="lobby-error" className="mt-4 text-rose-400 text-center font-bold">{error}</p>}
      </div>
    </div>
  );

  function startGameAction() {
    // Build initial CO-OP v2 game state: OPEN connected grid, per-player roomKey/alive, floor 1.
    const { generateCoopFloor } = require("../utils/coopMap");
    const { createCharacter } = require("../utils/gameLogic");

    const fresh = generateCoopFloor(1);
    const players = {};
    for (const m of lobby.members) {
      if (!m.classe_id) continue;
      players[m.user_id] = {
        user_id: m.user_id,
        name: m.name,
        classe_id: m.classe_id,
        character: createCharacter(m.classe_id, CLASSES, []),
        roomKey: fresh.startKey,
        alive: true,
      };
    }
    const initial = {
      version: 2,
      playerCount: lobby.members.length,
      floor: 1,
      grid: { rooms: fresh.rooms, startKey: fresh.startKey, bossKey: fresh.bossKey },
      players,
      combat: null,
      log: ["L'aventure coopérative commence ! Explorez librement, le combat est partagé."],
    };
    startGame(initial);
  }
};

export default Lobby;
